import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Link2, Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Alert, AlertDescription } from '../ui/alert'
import { useAuth } from '../../hooks/useAuth'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { useProgramaEnVivo } from '../../hooks/useProgramaEnVivo'
import { calculaTiempoEnVivo } from '../../lib/training/tiempoEnVivo'
import { inicializarAudio, setMuted, isMuted } from '../../lib/training/alertas'
import EnVivoCuentaRegresiva from './EnVivoCuentaRegresiva'
import EnVivoControles from './EnVivoControles'
import EnVivoSecciones from './EnVivoSecciones'
import PantallaTerminado from './PantallaTerminado'

const ModoEntrenamientoEnVivo = () => {
  const { sesionId } = useParams()
  const navigate = useNavigate()
  const { staff } = useAuth()
  const [muted, setMutedState] = useState(false)
  const [tick, setTick] = useState(Date.now())
  const wakeLockRef = useRef(null)
  const { confirm, ConfirmDialog } = useConfirmDialog()

  const {
    sesion,
    programa,
    secciones,
    estado,
    loading,
    error,
    terminadoRecien,
    iniciar,
    pausar,
    reanudar,
    saltar,
    anterior,
    extender,
    terminar
  } = useProgramaEnVivo({ sesionId, staffId: staff?.id })

  useEffect(() => {
    let cancelled = false
    const requestWakeLock = async () => {
      if (!('wakeLock' in navigator)) return
      try {
        const lock = await navigator.wakeLock.request('screen')
        if (cancelled) {
          lock.release()
          return
        }
        wakeLockRef.current = lock
      } catch (err) {
        console.warn('No se pudo activar wake lock:', err)
      }
    }
    requestWakeLock()
    return () => {
      cancelled = true
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {})
        wakeLockRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const i = setInterval(() => setTick(Date.now()), 1000)
    return () => clearInterval(i)
  }, [])

  useEffect(() => {
    setMutedState(isMuted())
  }, [])

  const toggleMute = () => {
    setMuted(!muted)
    setMutedState(!muted)
  }

  const handleIniciarConAudio = async () => {
    inicializarAudio()
    await iniciar()
  }

  const handleTerminar = async () => {
    const ok = await confirm(
      'Terminar entrenamiento',
      '¿Terminar el entrenamiento ahora? Se guardara el resumen.',
      { destructive: true, confirmText: 'Terminar' }
    )
    if (!ok) return
    await terminar()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver
        </Button>
      </div>
    )
  }

  if (!programa) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4">
        <p className="text-sm text-muted-foreground">
          Esta sesion no tiene programa asignado.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>Volver</Button>
      </div>
    )
  }

  if (secciones.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4">
        <p className="text-sm text-muted-foreground">
          Este programa no tiene secciones cronometradas.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>Volver</Button>
      </div>
    )
  }

  const tiempo = calculaTiempoEnVivo({ estado, secciones, ahora: tick })

  // Mostrar la pantalla de terminado cuando:
  // - acaba de terminarse en esta sesion
  // - el estado en vivo dice terminado
  // - hay un recorrido guardado y no hay estado en vivo activo (entro despues
  //   de haber terminado en una sesion previa)
  const yaTerminadoPrev = Boolean(sesion?.programa_recorrido) && !estado?.iniciado_en
  if (terminadoRecien || tiempo.terminado || yaTerminadoPrev) {
    return (
      <PantallaTerminado
        recorrido={sesion?.programa_recorrido}
        sesionId={sesionId}
        onCerrar={() => navigate(-1)}
      />
    )
  }

  const syncToken = sesion?.asignacion?.grupo_sync_token

  return (
    <>
      <div className="min-h-screen bg-muted/50 flex flex-col">
        <header className="px-4 py-3 flex items-center justify-between border-b bg-card">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver
          </Button>
          <div className="text-center flex-1 truncate px-2">
            <p className="text-xs text-muted-foreground">{sesion?.grupo?.nombre_del_grupo}</p>
            <p className="text-sm font-semibold text-foreground truncate">{programa.titulo}</p>
          </div>
          <div className="w-16" />
        </header>

        {syncToken && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-center text-xs text-blue-700">
            <Link2 className="h-3 w-3 mr-1" />
            Sincronizado entre grupos
          </div>
        )}

        <main className="flex-1 flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-md space-y-6">
            <EnVivoCuentaRegresiva estado={estado} secciones={secciones} />
            {tiempo.iniciado && (
              <EnVivoSecciones
                secciones={secciones}
                seccionIdx={tiempo.seccionIdx}
                porcentajeTotal={tiempo.porcentajeTotal}
              />
            )}
          </div>
        </main>

        <footer className="px-4 pb-6 pt-3 border-t bg-card">
          <div className="max-w-md mx-auto">
            <EnVivoControles
              iniciado={tiempo.iniciado}
              pausado={tiempo.pausado}
              terminado={tiempo.terminado}
              muted={muted}
              onIniciar={handleIniciarConAudio}
              onPausar={pausar}
              onReanudar={reanudar}
              onSaltar={saltar}
              onAnterior={anterior}
              onExtender={extender}
              onTerminar={handleTerminar}
              onToggleMute={toggleMute}
            />
          </div>
        </footer>
      </div>
      <ConfirmDialog />
    </>
  )
}

export default ModoEntrenamientoEnVivo
